<template>
  <nav
    class="navbar navbar-main navbar-expand-lg px-0 mx-4 shadow-none border-radius-xl"
    :class="
      this.$store.state.theme.isRTL ? 'top-0 position-sticky z-index-sticky' : ''
    "
    v-bind="$attrs"
    id="navbarBlur"
    data-scroll="true"
  >
    <!-- Main topbar row -->
    <div class="ps-1 pe-3 py-1 container-fluid navbar-row-grid align-items-center">

      <!-- Left side: breadcrumbs on desktop / logo + buttons on mobile -->
      <div class="flex-grow-0 d-flex align-items-center navbar-row-left">
        <breadcrumbs textWhite="text-white" class="w-auto d-none d-lg-flex" />
        <router-link to="/" class="d-lg-none me-2 navbar-mobile-logo-link">
          <img :src="$store.state.theme.darkMode ? logoShortLight : logoShortDark" alt="Graspil" class="navbar-mobile-logo" />
        </router-link>
        <!-- Mobile rotating buttons -->
        <div v-if="mobileButtonList.length === 1" class="d-lg-none me-2">
          <button
            class="btn btn-sm text-white mb-0 text-nowrap mobile-action-btn"
            :class="mobileButtonList[0].cls"
            @click="handleMobileButton(mobileButtonList[0].key)"
          >
            <i :class="mobileButtonList[0].icon + ' me-1'"></i>
            {{ mobileButtonList[0].label }}
          </button>
        </div>
        <div v-else-if="mobileButtonList.length > 1" class="d-lg-none me-2 mobile-btn-rotator">
          <transition name="btn-rotate">
            <button
              :key="activeButtonIndex"
              class="btn btn-sm text-white mb-0 text-nowrap mobile-action-btn"
              :class="mobileButtonList[activeButtonIndex].cls"
              @click="handleMobileButton(mobileButtonList[activeButtonIndex].key)"
            >
              <i :class="mobileButtonList[activeButtonIndex].icon + ' me-1'"></i>
              {{ mobileButtonList[activeButtonIndex].label }}
            </button>
          </transition>
        </div>
      </div>

      <!-- Center: quick-create button -->
      <div class="d-none d-lg-flex align-items-center navbar-create-center">
        <div class="dropdown">
          <a class="nav-pill" href="#" role="button" id="dropdownCreateLink" data-bs-toggle="dropdown" aria-expanded="false">
            <i class="fas fa-plus"></i>
          </a>

          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownCreateLink">
            <li>
              <router-link :to="{ name: 'EditCampaign' }" class="dropdown-item">
                <i class="fas fa-paper-plane" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Mailing.General.CreateCampaign')}}
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: 'report-builder' }" class="dropdown-item">
                <i class="fas fa-chart-bar" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Menu.CreateReport')}}
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: 'WorkflowNew' }" class="dropdown-item">
                <i class="fas fa-project-diagram" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Workflows.List.CreateNew')}}
              </router-link>
            </li>
            <li>
              <router-link :to="{ path: '/dashboards/new' }" class="dropdown-item">
                <i class="fas fa-th-large" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Menu.CreateDashboard')}}
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: 'cohorts', query: { create: '1' } }" class="dropdown-item">
                <i class="fas fa-users" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Cohorts.NewCohort')}}
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: 'Landings', query: { create: '1' } }" class="dropdown-item">
                <i class="fas fa-link" :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"></i>
                {{$t('Landings.Create')}}
              </router-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Right side: nav items -->
      <ul class="navbar-nav navbar-row-right justify-content-end flex-row align-items-center gap-2">
        <li
            v-if="!$store.state.init.user?.email"
            class="nav-item dropdown d-none d-lg-flex align-items-center"
        >
          <router-link
              :to="{ name: 'Profile' }"
              class="btn btn-warning btn-sm text-white mb-0 px-2 text-nowrap"
          >
            <i class="fas fa-at me-1"></i>
            {{$t("Profile.NeedEmailBtn")}}
          </router-link>
        </li>
        <li
            v-if="$store.state.init.can_activate_trial"
            class="nav-item dropdown d-none d-lg-flex align-items-center me-2"
        >
          <button
              class="btn btn-success btn-sm text-white mb-0 px-2 text-nowrap"
              @click="activateTrial"
          >
            <i class="fas fa-gift me-1"></i>
            <span class="d-none d-xl-inline">{{ $t('Navbar.ActivateTrial') }}</span>
            <span class="d-inline d-xl-none">{{ $t('Navbar.ActivateTrialMobile') }}</span>
          </button>
        </li>
        <!-- 2FA: desktop only (mobile version is in the left slot above) -->
        <li
            v-if="show2FAPromo"
            class="nav-item dropdown d-none d-lg-flex align-items-center"
        >
          <button
              class="btn btn-warning btn-sm text-white mb-0 px-2 text-nowrap"
              :title="$t('Navbar.Protect2FAHint')"
              @click="goTo2FA"
          >
            <i class="fas fa-shield-alt me-1"></i>
            <span class="d-none d-xl-inline">{{ $t('Navbar.Protect2FA') }}</span>
            <span class="d-inline d-xl-none">{{ $t('Navbar.Protect2FAMobile') }}</span>
          </button>
        </li>
        <!-- Novu: single instance, visible on all screen sizes -->
        <li v-if="showNovu" id="tour-notifications" class="nav-item d-flex align-items-center">
          <NovuInbox />
        </li>
        <!-- Theme toggle: Light / Auto / Dark -->
        <li id="tour-theme" class="nav-item d-flex align-items-center">
          <ThemeToggle />
        </li>
        <!-- Lang: visible on all screen sizes, same pill style as desktop -->
        <li id="tour-lang" class="nav-item d-flex align-items-center">
          <Lang class="nav-pill" />
        </li>
        <li v-if="navBalance != null" class="nav-item d-flex align-items-center">
          <router-link :to="navBalanceRoute" class="nav-pill">
            <i class="fas fa-wallet"></i>
            {{ navBalance }}
          </router-link>
        </li>

        <li class="nav-item d-none d-lg-flex align-items-center">
          <a href="https://graspil.com/ru/blog" class="nav-pill-plain" target="_blank">
            <i class="fas fa-book"></i>
            {{$t('Menu.Blog')}}
          </a>
        </li>

        <WhatsNew />
        <li id="tour-profile" class="nav-item d-none d-lg-flex align-items-center">
          <div class="dropdown">
            <a class="nav-pill-user" href="#" role="button" id="dropdownMenuLink" data-bs-toggle="dropdown" aria-expanded="false">
              {{$t('User.Profile')}}
              <span class="navbar-user-avatar"><i class="fa fa-user"></i></span>
            </a>

            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuLink">
              <li>
                <router-link
                    :to="{ name: 'Profile' }"
                    class="dropdown-item"
                >
                  <i
                      class="fas fa-user"
                      :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('User.Profile')}}</span>
                </router-link>
              </li>
              <li>
                <router-link
                    :to="{ name: 'BillingV2' }"
                    class="dropdown-item"
                >
                  <i
                      class="fas fa-wallet"
                      :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('Menu.Billing')}}</span>
                </router-link>
              </li>
              <li>
                <router-link
                    :to="{ name: 'Usage' }"
                    class="dropdown-item"
                >
                  <i
                      class="fas fa-chart-bar"
                      :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('Menu.Usage')}}</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'Resources' }"
                  class="dropdown-item"
                >
                  <i
                    class="fas fa-robot"
                    :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('Menu.Bots')}}</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'ApiKeys' }"
                  class="dropdown-item"
                >
                  <i
                    class="fas fa-key"
                    :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('ApiKeys.MenuTitle')}}</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'Affiliate' }"
                  class="dropdown-item"
                >
                  <i
                    class="fas fa-handshake"
                    :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  <span class="d-sm-inline d-none">{{$t('Menu.Affiliate')}}</span>
                </router-link>
              </li>
              <li>
                <hr class="mt-0 mb-0 horizontal dark" />
                <a class="dropdown-item" href="#" @click.stop="logout">
                  <i
                      class="fa fa-sign-out-alt"
                      :class="this.$store.state.theme.isRTL ? 'ms-sm-2' : 'me-sm-2'"
                  ></i>
                  {{$t('User.Logout')}}
                </a>
              </li>
            </ul>
          </div>
        </li>
        <!-- AI assistant: quick-ask peek that grows out of the pill; sending opens the full
             slide-in drawer. Available from any page. Kept last/rightmost. -->
        <li v-if="showNovu" class="nav-item d-flex align-items-center">
          <AssistantPeek />
        </li>
        <li class="nav-item d-none ps-3 d-flex align-items-center">
          <a
            href="#"
            @click="toggleSidebar"
            class="p-0 nav-link"
            id="iconNavbarSidenav"
          >
            <div class="sidenav-toggler-inner">
              <i class="sidenav-toggler-line bg-white"></i>
              <i class="sidenav-toggler-line bg-white"></i>
              <i class="sidenav-toggler-line bg-white"></i>
            </div>
          </a>
        </li>
      </ul>
    </div>

  </nav>
</template>
<script>
import Breadcrumbs from "../Breadcrumbs.vue";
import { mapMutations, mapActions } from "vuex";
import logoShortLight from "@/assets/img/graspil_short_logo_light.png";
import logoShortDark from "@/assets/img/graspil_short_logo_dark.png";
import UserService from "@/helpers/api/user.service";
import WhatsNew from "./_whats_new.vue"
import Lang from "./_lang.vue"
import NovuInbox from "@/components/NovuInbox.vue";
import ThemeToggle from "@/components/ThemeToggle.vue";
import AssistantPeek from "@/components/AssistantPeek.vue";
import { ElMessage, ElNotification } from "element-plus";
import billingService from "@/helpers/api/billing.service";
export default {
  name: "navbar",
  data() {
    return {
      showMenu: false,
      logoShortLight,
      logoShortDark,
      activeButtonIndex: 0,
      rotateTimer: null,
    };
  },
  props: ["minNav", "textWhite"],
  created() {
    this.minNav;
  },
  computed: {
    user: function () {
      return this.$store.state.init.user
    },
    mobileButtonList() {
      const list = [];
      if (this.$store.state.init.can_activate_trial) {
        list.push({
          key: 'trial',
          cls: 'btn-success',
          icon: 'fas fa-gift',
          label: this.$t('Navbar.ActivateTrialMobile'),
        });
      }
      if (this.show2FAPromo) {
        list.push({
          key: '2fa',
          cls: 'btn-warning',
          icon: 'fas fa-shield-alt',
          label: this.$t('Navbar.Protect2FAMobile'),
        });
      }
      return list;
    },
    showNovu: function () {
      return this.$store.state.init.user && this.$store.state.init.user.key;
    },
    show2FAPromo: function () {
      const user = this.$store.state.init.user;
      return user && user.has_2fa === false;
    },
    // Balance shown in the navbar — only for v2 users.
    navBalance: function () {
      const v2 = this.$store.state.init.user?.v2_billing;
      return v2 ? v2.balance.toFixed(2) : null;
    },
    navCurrency: function () {
      return this.$store.state.init.user?.v2_billing?.currency || '';
    },
    navBalanceRoute: function () {
      return { name: 'BillingV2' };
    },
  },
  methods: {
    ...mapMutations(["theme/navbarMinimize", "theme/toggleConfigurator"]),
    ...mapActions(["theme/toggleSidebarColor"]),

    toggleSidebar() {
      this['theme/toggleSidebarColor']("bg-white");
      this['theme/navbarMinimize']();
    },
    logout() {
      UserService.logout().then(() => {
        localStorage.removeItem('current_bot')
        localStorage.removeItem('newBotSetting')

        this.$router.go('Signin')
      })
    },
    goTo2FA() {
      ElMessage({ message: this.$t('Navbar.Protect2FAMessage'), type: 'warning', duration: 4000 });
      this.$router.push({ name: 'Profile' });
    },
    handleMobileButton(key) {
      if (key === 'trial') this.activateTrial();
      else if (key === '2fa') this.goTo2FA();
    },
    startRotation() {
      this.stopRotation();
      if (this.mobileButtonList.length > 1) {
        this.rotateTimer = setInterval(() => {
          this.activeButtonIndex = (this.activeButtonIndex + 1) % this.mobileButtonList.length;
        }, 5000);
      }
    },
    stopRotation() {
      if (this.rotateTimer) {
        clearInterval(this.rotateTimer);
        this.rotateTimer = null;
      }
    },
    activateTrial() {
      billingService.startDemoPeriod().then(() => {
        this.$store.commit('init/setCanActivateTrial', false);
        return this.$store.dispatch('init/reInit');
      }).then(() => {
        ElNotification({
          title: this.$t('General.Success'),
          message: 'Триал успешно активирован',
          type: 'success',
        });
      }).catch(err => {
        ElNotification({
          title: this.$t('General.Error'),
          message: err?.error || 'Ошибка при активации триала',
          type: 'error',
        });
        console.error(err);
      });
    }
  },
  mounted() {
    this.startRotation();
  },
  beforeUnmount() {
    this.stopRotation();
  },
  watch: {
    mobileButtonList(newList, oldList) {
      const newLen = newList.length;
      const oldLen = oldList ? oldList.length : 0;
      if (newLen === oldLen) return;
      this.activeButtonIndex = 0;
      if (newLen > 1) this.startRotation();
      else this.stopRotation();
    },
  },
  components: {
    Breadcrumbs, WhatsNew, Lang, NovuInbox, ThemeToggle, AssistantPeek
  },
};
</script>

<style scoped>
.navbar-mobile-logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 12px;
}
.navbar-mobile-logo {
  height: 22px;
  width: auto;
  object-fit: contain;
  display: block;
}

/* ── Mobile rotating button ── */

.mobile-action-btn {
  width: 100px;
  padding-left: 0.375rem;
  padding-right: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-btn-rotator {
  position: relative;
  width: 100px;
  height: 30px;
  overflow: hidden;
}

.mobile-btn-rotator .btn {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

/* New button slides in from the top over the current one */
.btn-rotate-enter-active {
  transition: transform 0.4s ease;
  z-index: 2;
}
.btn-rotate-leave-active {
  z-index: 1;
}
.btn-rotate-enter-from {
  transform: translateY(-100%);
}
.btn-rotate-enter-to {
  transform: translateY(0);
}
</style>
